import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Scale, ChevronDown } from "lucide-react";
import { useRealBalanceSheet } from "@/hooks/useRealBalanceSheet";
import { getCurrentUserCompanyId } from "@/lib/userUtils";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { BalanceSheetItemDialog } from "@/components/BalanceSheetItemDialog";
import { useQueryClient } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { TimePeriodFilter, type TimePeriodValue } from "@/components/TimePeriodFilter";
import { getDateRangeFromPreset, DEFAULT_TIME_PRESET } from "@/lib/timePeriodUtils";

const MONTHS = [
  "January", "February", "March", "April", "May", 
  "June", "July", "August", "September", "October", 
  "November", "December"
];

const getCurrentYearMonthKeys = () => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 12 }, (_, i) => {
    const month = String(i + 1).padStart(2, '0');
    return `${currentYear}-${month}`;
  });
};

const getLastThreeMonths = () => {
  const now = new Date();
  const months: string[] = [];
  
  for (let i = 2; i >= 0; i--) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    months.push(`${year}-${month}`);
  }
  
  return months;
};

const initialTimePeriod = (): TimePeriodValue => {
  const r = getDateRangeFromPreset(DEFAULT_TIME_PRESET);
  return { preset: DEFAULT_TIME_PRESET, startDate: r.start, endDate: r.end };
};

export const BalanceSheetPage = () => {
  const queryClient = useQueryClient();
  const [companyId, setCompanyId] = useState<string | undefined>();
  const [timePeriod, setTimePeriod] = useState<TimePeriodValue>(initialTimePeriod);
  const [selectedMonths, setSelectedMonths] = useState<string[]>(() => {
    const stored = localStorage.getItem('balance-sheet-selected-months');
    return stored ? JSON.parse(stored) : getLastThreeMonths();
  });

  const handleItemAdded = () => {
    queryClient.invalidateQueries({ queryKey: ['real-balance-sheet'] });
  };

  useEffect(() => {
    getCurrentUserCompanyId().then(setCompanyId);
  }, []);

  useEffect(() => {
    localStorage.setItem('balance-sheet-selected-months', JSON.stringify(selectedMonths));
  }, [selectedMonths]);

  const availableMonths = getCurrentYearMonthKeys();
  const filteredMonths = selectedMonths.length > 0 ? selectedMonths : availableMonths;

  // Convert month keys to date range for the hook
  const dateFrom = filteredMonths.length > 0 
    ? `${filteredMonths[0]}-01` 
    : `${new Date().getFullYear()}-01-01`;
  const dateTo = filteredMonths.length > 0
    ? (() => {
        const lastMonth = filteredMonths[filteredMonths.length - 1];
        const [year, month] = lastMonth.split('-');
        const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
        return `${lastMonth}-${lastDay}`;
      })()
    : new Date().toISOString().split('T')[0];

  const { data: balanceSheet, isLoading } = useRealBalanceSheet(companyId, dateFrom, dateTo);

  const toggleMonth = (monthKey: string) => {
    setSelectedMonths(prev => 
      prev.includes(monthKey) 
        ? prev.filter(m => m !== monthKey)
        : [...prev, monthKey].sort()
    );
  };

  const selectYTD = () => {
    setSelectedMonths(availableMonths);
  };

  const clearSelection = () => {
    setSelectedMonths([]);
  };

  const getMonthLabel = (monthKey: string) => {
    const [, month] = monthKey.split('-');
    return MONTHS[parseInt(month) - 1];
  };

  const formatCurrency = (value: number) => {
    const formatted = Math.abs(value).toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    return value < 0 ? `($${formatted})` : `$${formatted}`;
  };

  const LoadingSkeleton = () => <Skeleton className="h-6 w-24 inline-block" />;
  
  const displayValue = (value: number) => isLoading ? <LoadingSkeleton /> : formatCurrency(value);

  if (!balanceSheet && !isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-muted-foreground">No balance sheet data available</div>
      </div>
    );
  }
  
  const safeBalanceSheet = balanceSheet || {
    assets: { items: [], totalAssets: 0 },
    liabilities: { items: [], totalLiabilities: 0 },
    equity: { items: [], totalEquity: 0 }
  };

  // Categorize assets into current and non-current
  const currentAssets = safeBalanceSheet.assets.items.filter(item => 
    item.type.toLowerCase().includes('cash') ||
    item.type.toLowerCase().includes('receivable') ||
    item.type.toLowerCase().includes('inventory') ||
    item.type.toLowerCase().includes('prepaid')
  );
  
  const nonCurrentAssets = safeBalanceSheet.assets.items.filter(item => 
    item.type.toLowerCase().includes('equipment') ||
    item.type.toLowerCase().includes('property') ||
    item.type.toLowerCase().includes('ppe') ||
    item.type.toLowerCase().includes('intangible') ||
    item.type.toLowerCase().includes('long-term')
  );

  const totalCurrentAssets = currentAssets.reduce((sum, item) => sum + item.amount, 0);
  const totalNonCurrentAssets = nonCurrentAssets.reduce((sum, item) => sum + item.amount, 0);

  // Categorize liabilities into current and non-current
  const currentLiabilities = safeBalanceSheet.liabilities.items.filter(item =>
    item.type.toLowerCase().includes('payable') ||
    item.type.toLowerCase().includes('accrued') ||
    item.type.toLowerCase().includes('current')
  );

  const nonCurrentLiabilities = safeBalanceSheet.liabilities.items.filter(item =>
    item.type.toLowerCase().includes('long-term') ||
    item.type.toLowerCase().includes('debt') ||
    item.type.toLowerCase().includes('loan')
  );

  const totalCurrentLiabilities = currentLiabilities.reduce((sum, item) => sum + item.amount, 0);
  const totalNonCurrentLiabilities = nonCurrentLiabilities.reduce((sum, item) => sum + item.amount, 0);

  const isBalanced = Math.abs(safeBalanceSheet.assets.totalAssets - 
    (safeBalanceSheet.liabilities.totalLiabilities + safeBalanceSheet.equity.totalEquity)) < 1;

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap justify-between items-start gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Scale className="h-8 w-8" />
            Balance Sheet
          </h1>
          <p className="text-muted-foreground mt-1">
            {filteredMonths.length > 0 ? `As of ${getMonthLabel(filteredMonths[filteredMonths.length - 1])} ${new Date().getFullYear()}` : 'No data'}
          </p>
        </div>
        <TimePeriodFilter value={timePeriod} onChange={setTimePeriod} />
      </div>

      {/* Month Selection Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Select Months</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-3">
            {availableMonths.map(monthKey => (
              <Button
                key={monthKey}
                variant={selectedMonths.includes(monthKey) ? "default" : "outline"}
                size="sm"
                onClick={() => toggleMonth(monthKey)}
                className="min-w-[80px]"
              >
                {getMonthLabel(monthKey)}
              </Button>
            ))}
          </div>
          <div className="flex gap-2">
            <Button onClick={selectYTD} variant="secondary" size="sm">
              Select YTD
            </Button>
            <Button onClick={clearSelection} variant="outline" size="sm">
              Clear Selection
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Financial Position</CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion type="multiple" defaultValue={["assets", "liabilities", "equity"]} className="space-y-2">
            {/* ASSETS Accordion */}
            <AccordionItem value="assets" className="border rounded-lg px-4 bg-card">
              <AccordionTrigger className="hover:no-underline py-4">
                <div className="flex justify-between items-center w-full pr-4">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-xl text-primary">ASSETS</h3>
                    <BalanceSheetItemDialog subsection="Assets" onSuccess={handleItemAdded} />
                  </div>
                  <span className="font-bold text-xl text-profit">
                    {displayValue(safeBalanceSheet.assets.totalAssets)}
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="space-y-3 pb-4">
                {/* Current Assets */}
                <Collapsible defaultOpen>
                  <CollapsibleTrigger className="w-full">
                    <div className="flex items-center gap-2 rounded-md bg-green-50/50 px-4 py-3 hover:bg-green-50/70 transition-colors">
                      <ChevronDown className="h-4 w-4 transition-transform duration-200" />
                      <div className="flex-1 flex justify-between">
                        <span className="font-semibold">Current Assets</span>
                        <span className="font-semibold text-profit">{displayValue(totalCurrentAssets)}</span>
                      </div>
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pl-8 pt-2 space-y-1">
                    {currentAssets.length > 0 ? (
                      currentAssets.map((item, index) => (
                        <div key={index} className="flex justify-between text-sm py-1">
                          <span className="text-muted-foreground">{item.type}</span>
                          <span className="font-medium min-w-[100px] text-right">{displayValue(item.amount)}</span>
                        </div>
                      ))
                    ) : (
                      <div className="text-xs text-muted-foreground">No current assets recorded</div>
                    )}
                    <div className="flex justify-between text-sm py-2 border-t mt-2">
                      <span className="font-semibold">Total Current Assets</span>
                      <span className="font-semibold">{displayValue(totalCurrentAssets)}</span>
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                {/* Non-Current Assets */}
                <Collapsible defaultOpen>
                  <CollapsibleTrigger className="w-full">
                    <div className="flex items-center gap-2 rounded-md bg-emerald-50/50 px-4 py-3 hover:bg-emerald-50/70 transition-colors">
                      <ChevronDown className="h-4 w-4 transition-transform duration-200" />
                      <div className="flex-1 flex justify-between">
                        <span className="font-semibold">Non-Current Assets</span>
                        <span className="font-semibold text-profit">{displayValue(totalNonCurrentAssets)}</span>
                      </div>
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pl-8 pt-2 space-y-1">
                    {nonCurrentAssets.length > 0 ? (
                      nonCurrentAssets.map((item, index) => (
                        <div key={index} className="flex justify-between text-sm py-1">
                          <span className="text-muted-foreground">{item.type}</span>
                          <span className="font-medium min-w-[100px] text-right">{displayValue(item.amount)}</span>
                        </div>
                      ))
                    ) : (
                      <div className="text-xs text-muted-foreground">No non-current assets recorded</div>
                    )}
                    <div className="flex justify-between text-sm py-2 border-t mt-2">
                      <span className="font-semibold">Total Non-Current Assets</span>
                      <span className="font-semibold">{displayValue(totalNonCurrentAssets)}</span>
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                {/* Total Assets */}
                <div className="flex justify-between items-center px-4 py-3 border-t mt-2 pt-3 bg-slate-100 rounded-md">
                  <span className="font-bold text-lg">TOTAL ASSETS</span>
                  <span className="font-bold text-lg text-profit">{displayValue(safeBalanceSheet.assets.totalAssets)}</span>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* LIABILITIES Accordion */}
            <AccordionItem value="liabilities" className="border rounded-lg px-4 bg-card">
              <AccordionTrigger className="hover:no-underline py-4">
                <div className="flex justify-between items-center w-full pr-4">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-xl text-primary">LIABILITIES</h3>
                    <BalanceSheetItemDialog subsection="Liabilities" onSuccess={handleItemAdded} />
                  </div>
                  <span className="font-bold text-xl text-loss">
                    {displayValue(safeBalanceSheet.liabilities.totalLiabilities)}
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="space-y-3 pb-4">
                {/* Current Liabilities */}
                <Collapsible defaultOpen>
                  <CollapsibleTrigger className="w-full">
                    <div className="flex items-center gap-2 rounded-md bg-red-50/50 px-4 py-3 hover:bg-red-50/70 transition-colors">
                      <ChevronDown className="h-4 w-4 transition-transform duration-200" />
                      <div className="flex-1 flex justify-between">
                        <span className="font-semibold">Current Liabilities</span>
                        <span className="font-semibold text-loss">{displayValue(totalCurrentLiabilities)}</span>
                      </div>
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pl-8 pt-2 space-y-1">
                    {currentLiabilities.length > 0 ? (
                      currentLiabilities.map((item, index) => (
                        <div key={index} className="flex justify-between text-sm py-1">
                          <span className="text-muted-foreground">{item.type}</span>
                          <span className="font-medium min-w-[100px] text-right">{displayValue(item.amount)}</span>
                        </div>
                      ))
                    ) : (
                      <div className="text-xs text-muted-foreground">No current liabilities recorded</div>
                    )}
                    <div className="flex justify-between text-sm py-2 border-t mt-2">
                      <span className="font-semibold">Total Current Liabilities</span>
                      <span className="font-semibold">{displayValue(totalCurrentLiabilities)}</span>
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                {/* Non-Current Liabilities */}
                <Collapsible defaultOpen>
                  <CollapsibleTrigger className="w-full">
                    <div className="flex items-center gap-2 rounded-md bg-rose-50/50 px-4 py-3 hover:bg-rose-50/70 transition-colors">
                      <ChevronDown className="h-4 w-4 transition-transform duration-200" />
                      <div className="flex-1 flex justify-between">
                        <span className="font-semibold">Non-Current Liabilities</span>
                        <span className="font-semibold text-loss">{displayValue(totalNonCurrentLiabilities)}</span>
                      </div>
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pl-8 pt-2 space-y-1">
                    {nonCurrentLiabilities.length > 0 ? (
                      nonCurrentLiabilities.map((item, index) => (
                        <div key={index} className="flex justify-between text-sm py-1">
                          <span className="text-muted-foreground">{item.type}</span>
                          <span className="font-medium min-w-[100px] text-right">{displayValue(item.amount)}</span>
                        </div>
                      ))
                    ) : (
                      <div className="text-xs text-muted-foreground">No non-current liabilities recorded</div>
                    )}
                    <div className="flex justify-between text-sm py-2 border-t mt-2">
                      <span className="font-semibold">Total Non-Current Liabilities</span>
                      <span className="font-semibold">{displayValue(totalNonCurrentLiabilities)}</span>
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                {/* Total Liabilities */}
                <div className="flex justify-between items-center px-4 py-3 border-t mt-2 pt-3 bg-slate-100 rounded-md">
                  <span className="font-bold text-lg">TOTAL LIABILITIES</span>
                  <span className="font-bold text-lg text-loss">{displayValue(safeBalanceSheet.liabilities.totalLiabilities)}</span>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* EQUITY Accordion */}
            <AccordionItem value="equity" className="border rounded-lg px-4 bg-card">
              <AccordionTrigger className="hover:no-underline py-4">
                <div className="flex justify-between items-center w-full pr-4">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-xl text-primary">EQUITY</h3>
                    <BalanceSheetItemDialog subsection="Equity" onSuccess={handleItemAdded} />
                  </div>
                  <span className={`font-bold text-xl ${safeBalanceSheet.equity.totalEquity >= 0 ? 'text-profit' : 'text-loss'}`}>
                    {displayValue(safeBalanceSheet.equity.totalEquity)}
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="space-y-3 pb-4">
                <Collapsible defaultOpen>
                  <CollapsibleTrigger className="w-full">
                    <div className="flex items-center gap-2 rounded-md bg-blue-50/50 px-4 py-3 hover:bg-blue-50/70 transition-colors">
                      <ChevronDown className="h-4 w-4 transition-transform duration-200" />
                      <div className="flex-1 flex justify-between">
                        <span className="font-semibold">Owner's Equity</span>
                        <span className={`font-semibold ${safeBalanceSheet.equity.totalEquity >= 0 ? 'text-profit' : 'text-loss'}`}>
                          {displayValue(safeBalanceSheet.equity.totalEquity)}
                        </span>
                      </div>
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pl-8 pt-2 space-y-1">
                    <div className="flex justify-between text-sm py-1">
                      <span className="text-muted-foreground">Retained Earnings (Assets - Liabilities)</span>
                      <span className={`font-medium ${safeBalanceSheet.equity.totalEquity >= 0 ? 'text-profit' : 'text-loss'}`}>
                        {displayValue(safeBalanceSheet.equity.totalEquity)}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-2">
                      Represents the cumulative net income retained in the business
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                {/* Total Equity */}
                <div className="flex justify-between items-center px-4 py-3 border-t mt-2 pt-3 bg-slate-100 rounded-md">
                  <span className="font-bold text-lg">TOTAL EQUITY</span>
                  <span className={`font-bold text-lg ${safeBalanceSheet.equity.totalEquity >= 0 ? 'text-profit' : 'text-loss'}`}>
                    {displayValue(safeBalanceSheet.equity.totalEquity)}
                  </span>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* Total Liabilities & Equity */}
          <div className="mt-4 flex justify-between items-center px-4 py-3 bg-slate-200 rounded-md border-2 border-double">
            <span className="font-bold text-xl">TOTAL LIABILITIES & EQUITY</span>
            <span className="font-bold text-xl">
              {displayValue(safeBalanceSheet.liabilities.totalLiabilities + safeBalanceSheet.equity.totalEquity)}
            </span>
          </div>

          {/* Balance Check */}
          <div className="mt-6 p-4 rounded-lg bg-muted/30 border">
            <div className="flex justify-between items-center">
              <span className="font-semibold">Balance Check:</span>
              <span className={`font-bold ${isBalanced ? 'text-profit' : 'text-loss'}`}>
                {isBalanced ? '✓ Balanced' : '✗ Not Balanced'}
              </span>
            </div>
            <div className="text-sm text-muted-foreground mt-2">
              Assets = Liabilities + Equity
            </div>
            <div className="text-sm text-muted-foreground">
              {displayValue(safeBalanceSheet.assets.totalAssets)} = {displayValue(safeBalanceSheet.liabilities.totalLiabilities)} + {displayValue(safeBalanceSheet.equity.totalEquity)}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
